import React, { useState, memo, useCallback } from 'react';
import SingleInput from './single-input';
import { ReactComponent as Error } from '../../../assets/svg/icon_error.svg';
import './otp-input.scss';

export function OTPInputComponent({
  name,
  length,
  inputClassName,
  onChangeOTP,
  autoFocus,
  attempted,
  totalAttempts = 5,
  errorMessage,
  disabled
}) {
  const [activeInput, setActiveInput] = useState(0);
  const [otpValues, setOTPValues] = useState(Array(length).fill(''));

  const handleOtpChange = useCallback(
    otp => {
      const otpValue = otp.join('');
      onChangeOTP(otpValue);
    },
    [onChangeOTP]
  );

  // Change OTP value at focussing input
  const changeCodeAtFocus = useCallback(
    str => {
      const updatedOTPValues = [...otpValues];
      updatedOTPValues[activeInput] = str[0] || '';
      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
    },
    [activeInput, handleOtpChange, otpValues]
  );

  // Focus `inputIndex` input
  const focusInput = useCallback(
    inputIndex => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
      setActiveInput(selectedIndex);
    },
    [length]
  );

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1);
  }, [activeInput, focusInput]);

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  // Handle onFocus input
  const handleOnFocus = useCallback(
    index => () => {
      focusInput(index);
    },
    [focusInput]
  );

  // Handle onChange value for each input
  const handleOnChange = useCallback(
    e => {
      let enteredValue = e.currentTarget.value;
      const numbersOnly = /[0-9]/g;
      const enteredNumber = enteredValue.match(numbersOnly);

      if (!enteredNumber) {
        e.preventDefault();
        return;
      }
      changeCodeAtFocus(...enteredNumber);
      focusNextInput();
    },
    [changeCodeAtFocus, focusNextInput]
  );

  // Hanlde onBlur input
  const onBlur = useCallback(() => {
    setActiveInput(-1);
    return false;
  }, []);

  // Handle onKeyDown input
  const handleOnKeyDown = useCallback(
    e => {
      switch (e.key) {
        case 'Backspace':
        case 'Delete': {
          e.preventDefault();
          if (otpValues[activeInput]) {
            changeCodeAtFocus('');
          } else {
            focusPrevInput();
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          focusNextInput();
          break;
        }
        case ' ': {
          e.preventDefault();
          break;
        }
        default:
          break;
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]
  );

  return (
    <>
      <div className={!errorMessage ? 'otp-wrapper' : 'otp-wrapper error'}>
        {/* <p className="otp-attempts">
          {attempted} / {totalAttempts} attempts
        </p> */}
        {Array(length)
          .fill('')
          .map((_, index) => (
            <SingleInput
              name={name}
              key={`SingleInput-${index}`}
              focus={activeInput === index}
              autoFocus={autoFocus}
              value={otpValues && otpValues[index]}
              onFocus={handleOnFocus(index)}
              onChange={handleOnChange}
              onKeyDown={handleOnKeyDown}
              onBlur={onBlur}
              className={inputClassName || ' otpInput'}
              disabled={disabled}
            />
          ))}
      </div>
      {errorMessage && errorMessage.length > 0 ? (
        <div className="otp-error-wrapper">
          <Error className="error-icon" /> <span dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
        </div>
      ) : null}
    </>
  );
}

const OTPInput = memo(OTPInputComponent);
export default OTPInput;
